/**
 * AI Governance — domain constants for the AI Use Case Register.
 *
 * Ported 1:1 from the FAIR app's AI Governance module. The copy in this file
 * (AI type descriptions, GDPR legal-basis wording, guidance text and worked
 * examples) is the product — do not paraphrase or shorten it.
 *
 * Badge maps have been translated from the old Bootstrap `bg-label-*` classes to
 * Nimbus design-system tone names. Consumers render them as:
 *   <span className={`np-chip np-tone-${tone}`}>…</span>
 */

import type {
  AiType,
  AiUseCaseStatus,
  DataCategory,
  DataField,
  DataSensitivityAnswer,
  SensitivityLevel,
} from '../types/models';

// ─── AI Type ──────────────────────────────────────────────────────────────────

export const AI_TYPES: AiType[] = ['business-app', 'generative-ai', 'code-agent', 'agentic-ai', 'product-ai'];

export const AI_TYPE_LABELS: Record<AiType, string> = {
  'business-app':   'Business App AI',
  'generative-ai':  'Generative AI',
  'code-agent':     'AI Code Agent',
  'agentic-ai':     'Agentic AI',
  'product-ai':     'Product AI',
};

export const AI_TYPE_DESCRIPTIONS: Record<AiType, { short: string; detail: string; examples: string[] }> = {
  'business-app': {
    short: 'AI features built into software you already use',
    detail: 'Your existing tools — like Microsoft 365, Salesforce, or Workday — have AI capabilities built in. You want to switch them on or use them more intentionally.',
    examples: ['Microsoft Copilot in Word/Excel', 'Salesforce Einstein for lead scoring', 'Zendesk AI for ticket triage'],
  },
  'generative-ai': {
    short: 'Using AI platforms to generate content or analysis',
    detail: 'Staff are using ChatGPT, Claude, Gemini, or similar platforms to help with writing, summarising, research, or analysis in their day-to-day work.',
    examples: ['Drafting emails and reports with ChatGPT', 'Summarising lengthy documents with Claude', 'Generating images or presentations with DALL·E'],
  },
  'code-agent': {
    short: 'AI that writes or reviews code for your developers',
    detail: 'Your development team wants to use AI coding assistants to write, review, test, or refactor code faster and with fewer errors.',
    examples: ['GitHub Copilot for code completion', 'Cursor AI for code generation', 'AI-assisted code review tools'],
  },
  'agentic-ai': {
    short: 'Autonomous AI that takes actions on your behalf',
    detail: 'More advanced than a chatbot — this AI can plan, make decisions, and take actions (like browsing the web, sending emails, or calling APIs) without a human approving each step.',
    examples: ['An AI agent that monitors inboxes and routes requests', 'An AI workflow that researches and books travel automatically', 'An AI that monitors systems and raises support tickets'],
  },
  'product-ai': {
    short: 'AI you are building into your own product or service',
    detail: 'You are building AI features into a product or service that your customers or users will interact with. You are the one delivering the AI, not just consuming it.',
    examples: ['Adding an AI chatbot to your customer-facing app', 'Building AI-powered recommendations into your platform', 'Offering an AI-assisted analysis tool to your clients'],
  },
};

/** RemixIcon name per AI type (ported verbatim from the source). */
export const AI_TYPE_ICONS: Record<AiType, string> = {
  'business-app':  'ri-building-4-line',
  'generative-ai': 'ri-sparkling-2-line',
  'code-agent':    'ri-code-box-line',
  'agentic-ai':    'ri-robot-2-line',
  'product-ai':    'ri-rocket-2-line',
};

/** Nimbus tone per AI type — replaces the source's `bg-label-*` + hex accents. */
export const AI_TYPE_TONE: Record<AiType, string> = {
  'business-app':  'primary',
  'generative-ai': 'warning',
  'code-agent':    'info',
  'agentic-ai':    'danger',
  'product-ai':    'success',
};

// ─── Status ───────────────────────────────────────────────────────────────────

// `rejected` is additive, from the AI Act work: the ported lifecycle had no way to say no, so
// "approval" was just `submitted → active` with nothing recorded. `approved` is additive too,
// from the "active must mean fully compliant" redesign: `active` is now reserved for "every
// applicable compliance item is done" and is written ONLY by the backend's own
// _refresh_compliance_and_maybe_activate - never a client-requested transition target (see
// AI_STATUS_TRANSITIONS below, and lambda/prod-nimbus-ai-use-cases/lambda_function.py's
// ALLOWED_TRANSITIONS). Approving a use case lands on `approved` ("a human said yes, compliance
// work may still be outstanding"); it self-promotes to `active` the moment that work is done.
export const AI_STATUSES: AiUseCaseStatus[] = ['draft', 'submitted', 'approved', 'rejected', 'active', 'retired'];

export const AI_STATUS_LABELS: Record<AiUseCaseStatus, string> = {
  draft:     'Draft',
  submitted: 'Submitted',
  approved:  'Approved',
  rejected:  'Rejected',
  active:    'Active',
  retired:   'Retired',
};

/** Nimbus tone per status — replaces the source's AI_STATUS_BADGE (`bg-label-*`). */
export const AI_STATUS_TONE: Record<AiUseCaseStatus, string> = {
  draft:     'neutral',
  submitted: 'warning',
  // Distinct from both `submitted` (still under review) and `active` (fully compliant) - this
  // is "decided, compliance work in progress", a real in-between state now that it exists.
  approved:  'info',
  rejected:  'danger',
  active:    'success',
  retired:   'danger',
};

/** Which statuses a use case may move to next. `active` deliberately appears in NO list here -
 * it is reached only by automatic promotion, never a client-requested transition. */
export const AI_STATUS_TRANSITIONS: Record<AiUseCaseStatus, AiUseCaseStatus[]> = {
  draft:     ['submitted'],
  submitted: ['approved', 'rejected', 'draft'],
  // Not terminal: a rejected use case goes back to draft to be revised and resubmitted, which
  // re-runs every wizard gate on the way through. There is deliberately no rejected → approved.
  rejected:  ['draft'],
  approved:  ['retired'],
  active:    ['retired'],
  retired:   [],
};

/** Button label for a transition *into* the given status. `active` is unreachable as a target
 * (see AI_STATUS_TRANSITIONS) - its entry exists only because this is a Record over every
 * status, and is never actually rendered. */
export const AI_STATUS_ACTION_LABELS: Record<AiUseCaseStatus, string> = {
  submitted: 'Submit for Review',
  approved:  'Approve',
  rejected:  'Reject',
  active:    'Mark Active',
  retired:   'Retire',
  draft:     'Return to Draft',
};

/** Shown on a transition button in place of AI_STATUS_ACTION_LABELS while the request is in
 * flight. Approving in particular can run for the better part of a minute server-side (it
 * drafts the DPIA/Processing Activity/Subprocessor documents via Bedrock before responding) -
 * a disabled button with no other feedback reads as broken rather than working. */
export const AI_STATUS_PENDING_LABELS: Record<AiUseCaseStatus, string> = {
  submitted: 'Submitting…',
  approved:  'Approving…',
  rejected:  'Rejecting…',
  active:    'Marking Active…',
  retired:   'Retiring…',
  draft:     'Returning to Draft…',
};

// ─── Data & Privacy ───────────────────────────────────────────────────────────

export const LEGAL_BASIS_OPTIONS: { value: string; label: string; description: string }[] = [
  { value: 'consent',              label: 'Consent',               description: 'The person has given clear consent to process their data for this specific purpose.' },
  { value: 'contract',             label: 'Contract',              description: 'Processing is necessary to perform a contract with the individual, or at their request before entering a contract.' },
  { value: 'legal-obligation',     label: 'Legal obligation',      description: 'Processing is necessary to comply with the law (not including contractual obligations).' },
  { value: 'vital-interests',      label: 'Vital interests',       description: 'Processing is necessary to protect someone\'s life.' },
  { value: 'public-task',          label: 'Public task',           description: 'Processing is necessary to perform a task in the public interest or for official functions.' },
  { value: 'legitimate-interests', label: 'Legitimate interests',  description: 'Processing is necessary for your legitimate interests or those of a third party, unless overridden by the data subject\'s rights.' },
];

export const DATA_CATEGORY_LABELS: Record<DataCategory, string> = {
  personal:    'Personal / Identity',
  financial:   'Financial',
  health:      'Health / Medical',
  technical:   'Technical / System',
  operational: 'Operational / Business',
  other:       'Other',
};

export const DATA_CATEGORIES = Object.keys(DATA_CATEGORY_LABELS) as DataCategory[];

// Moved to constants/tone.ts (the shared Phase 7 tone vocabulary — see that file's own
// doc-comment) and re-exported here so every existing importer of these two names keeps
// working unchanged.
export { SENSITIVITY_LABELS, SENSITIVITY_TONE } from './tone';

export const SENSITIVITY_LEVELS: SensitivityLevel[] = ['public', 'internal', 'confidential', 'restricted'];

/**
 * Whether a data field is personal / special-category data — three answers, not two.
 * `customer_controlled` is for a field whose content the system doesn't fix, most often a
 * file upload or free-text box, where the customer decides what goes in it. Shared between
 * `isPersonalData` and `isSpecialCategory` on `DataField`: both are the same kind of answer
 * to the same kind of question.
 */
export const DATA_SENSITIVITY_LABELS: Record<DataSensitivityAnswer, string> = {
  no:  'Not personal',
  yes: 'Personal data',
  customer_controlled: 'Customer controlled',
};

export const DATA_SENSITIVITY_ANSWERS = Object.keys(DATA_SENSITIVITY_LABELS) as DataSensitivityAnswer[];

/** A step down from the confirmed tone: 'customer_controlled' is a real risk to flag, but it
 *  is not the same claim as a confirmed 'yes', so it does not share a tone. Personal data
 *  only — special category is a plain boolean and has no "possible" tier of its own. */
export const DATA_SENSITIVITY_TONE: Record<DataSensitivityAnswer, string> = {
  no:  'neutral',
  yes: 'warning',
  customer_controlled: 'info',
};

export const DATA_SENSITIVITY_HINT: Record<DataSensitivityAnswer, string> = {
  no:  '',
  yes: '',
  customer_controlled:
    "Depends on what the customer uploads or enters — the platform doesn't control the "
    + "content, so this can't be answered with certainty in advance.",
};

/**
 * A compact risk indicator for one data field, combining both flags into a single chip —
 * used everywhere a field is shown in a list or table rather than its own full editor row
 * (the detail page's meta table, the PDF export, and any future register that lists
 * `DataField`s). Kept in exactly one place because two independent copies of the same
 * priority rule already drifted once this session, in the AI Act automated-decision helper —
 * see `needsAutomatedDecisionDetails` in `constants/aiAct.ts`.
 *
 * Priority, highest first: special category (a plain boolean) > personal data confirmed >
 * personal data possible > neither. Special category outranks personal data unconditionally —
 * it isn't gated on what isPersonalData says, because a stale or inconsistent record could
 * otherwise hide a real Article 9 flag behind a personal-data value that disagrees with it.
 */
export const dataFieldRiskCell = (
  f: Pick<DataField, 'isPersonalData' | 'isSpecialCategory'>,
): { text: string; tone: string } => {
  // isSpecialCategory is a plain boolean — a confirmed Article 9 flag outranks even a
  // confirmed personal-data answer, which is why this check runs first and unconditionally,
  // independent of what isPersonalData says.
  if (f.isSpecialCategory) return { text: 'Special', tone: 'danger' };
  if (f.isPersonalData === 'yes') return { text: 'Yes', tone: DATA_SENSITIVITY_TONE.yes };
  if (f.isPersonalData === 'customer_controlled') {
    return { text: 'Yes?', tone: DATA_SENSITIVITY_TONE.customer_controlled };
  }
  return { text: 'No', tone: 'neutral' };
};

/** Suggestions for the "Data source" input on Step 5 (datalist / quick-pick). */
export const DATA_SOURCE_SUGGESTIONS: string[] = [
  'HubSpot CRM',
  'Call recording / transcript',
  'Email correspondence',
  'Product usage / analytics',
  'Support tickets',
  'NPS / CSAT surveys',
  'LinkedIn integration',
  'Third-party enrichment provider',
  'Customer-submitted form',
  'Internal database',
  'Public web data',
];

// ─── Step 2 — Identity & RACI ─────────────────────────────────────────────────

/** Suggestions for the "Department / Team" input on Step 2 (datalist / quick-pick). */
export const DEPARTMENT_SUGGESTIONS: string[] = [
  'IT', 'Engineering', 'Product', 'Finance', 'Legal', 'HR', 'Operations',
  'Sales', 'Marketing', 'Customer Success', 'Data & Analytics', 'Compliance',
];

export interface RaciRoleSpec {
  /** The AiUseCase key this role writes to. */
  field: 'owner' | 'raciResponsible' | 'raciInfoSec' | 'raciLegal' | 'raciInformed';
  /** Field label. */
  label: string;
  /** RACI position shown as a chip next to the label. */
  raci: 'Accountable' | 'Responsible' | 'Consulted' | 'Informed';
  /** Nimbus tone for the RACI chip. */
  tone: string;
  /** Helper copy explaining what this role does. */
  description: string;
  placeholder: string;
  required: boolean;
  /** True for a single real person (searchable People-register picker, stores a personId).
   *  False for raciInformed, which names a group/audience in free text (its own placeholder
   *  is a list - "Sales team, Customer Success Managers..." - not one person to look up). */
  personPicker: boolean;
}

/** The five RACI roles, in display order, with the source's helper copy verbatim. */
export const RACI_ROLES: RaciRoleSpec[] = [
  {
    field: 'owner',
    label: 'Business Owner',
    raci: 'Accountable',
    tone: 'danger',
    description: 'Signs off on this use case and owns the outcome. Accountable if something goes wrong.',
    placeholder: 'e.g. Sarah Johnson, Head of Customer Success',
    required: true,
    personPicker: true,
  },
  {
    field: 'raciResponsible',
    label: 'Builder / Admin',
    raci: 'Responsible',
    tone: 'warning',
    description: 'Who builds and maintains it? (e.g. RevOps, IT, a developer)',
    placeholder: 'e.g. Alex Rivera, RevOps Engineer',
    required: true,
    personPicker: true,
  },
  {
    field: 'raciInfoSec',
    label: 'Information Security',
    raci: 'Consulted',
    tone: 'info',
    description: 'Information Security — reviews security posture, vendor risk, data access controls',
    placeholder: 'e.g. James Park, Head of Information Security',
    required: true,
    personPicker: true,
  },
  {
    field: 'raciLegal',
    label: 'Legal / Data Protection',
    raci: 'Consulted',
    tone: 'info',
    description: 'Legal / Data Protection — reviews privacy obligations, GDPR, DPA requirements',
    placeholder: 'e.g. DPO or Legal Counsel name',
    required: false,
    personPicker: true,
  },
  {
    field: 'raciInformed',
    label: 'End Users / Stakeholders',
    raci: 'Informed',
    tone: 'neutral',
    description: 'Who uses the AI output and needs to know it exists and its limitations?',
    placeholder: 'e.g. Sales team, Customer Success Managers, Sales Director',
    required: false,
    personPicker: false,
  },
];

// ─── Step 3 — Business Case ───────────────────────────────────────────────────

export interface BusinessCaseFieldSpec {
  /** The AiUseCase key this field writes to (rich text / HTML). */
  field: 'businessProblem' | 'businessValue' | 'alternatives';
  label: string;
  guidance: string;
  placeholder: string;
  /** Worked example, revealed by the "See example" toggle. */
  example: string;
  required: boolean;
}

/** The three guided rich-text fields on Step 3, with worked examples verbatim. */
export const BUSINESS_CASE_FIELDS: BusinessCaseFieldSpec[] = [
  {
    field: 'businessProblem',
    label: 'What problem are you trying to solve?',
    guidance: "Describe the current situation and what's not working. Include numbers where you have them — time spent, error rates, customer complaints, cost, etc.",
    placeholder: 'Describe the problem this AI is meant to solve…',
    example: 'Our customer support team receives around 400 tickets per week. Roughly 60% of these are variations of the same 25 questions — password resets, delivery tracking, and refund status. Each ticket takes an agent 8–12 minutes to resolve. This consumes approximately 40% of total agent capacity and leaves complex issues waiting an average of 6 hours for first response.',
    required: true,
  },
  {
    field: 'businessValue',
    label: 'What value do you expect AI to deliver?',
    guidance: "Describe the expected benefits — time saved, cost reduced, quality improved, risk reduced. Quantify where possible. If you can't put a number on it, explain why it still matters.",
    placeholder: 'Describe the expected benefit or value…',
    example: 'Automating responses to the top 25 FAQ categories is expected to resolve 55–65% of tickets without agent involvement. This would free approximately 160 agent-hours per week, reduce average first-response time from 6 hours to under 5 minutes for qualifying queries, and reduce cost-per-ticket by an estimated 45%. Agents can focus on complex, high-value interactions.',
    required: true,
  },
  {
    field: 'alternatives',
    label: 'What alternatives did you consider?',
    guidance: 'What else could solve this problem without AI? Hiring more staff, better processes, existing tools, or simpler automation? Explain why AI was chosen over these alternatives.',
    placeholder: 'Describe what else was considered…',
    example: 'We considered: (1) Hiring 2 additional agents — cost £65k/year, does not scale. (2) Improving self-service documentation — already done, customers still raise tickets. (3) Rule-based chatbot — evaluated but too rigid for natural language variance in customer queries. AI was chosen because it can handle language variation, learn over time, and integrate with our existing helpdesk system.',
    required: true,
  },
];

/** Footnote shown beneath the Step 3 fields. */
export const BUSINESS_CASE_FOOTNOTE =
  "Not sure how to answer? That's worth pausing on — if you can't articulate the problem and value clearly, it may be too early to commit to an AI implementation.";

// ─── Step 4 — Outcomes ────────────────────────────────────────────────────────

/** Quick-pick outcome suggestions offered beneath the outcome rows. */
export const OUTCOME_HINTS: string[] = [
  'Reduce average handling time from X minutes to Y minutes',
  'Deflect 50% of tier-1 support tickets without human involvement',
  'Cut time-to-first-draft from 3 hours to 20 minutes',
  'Reduce manual data entry errors by 80%',
  'Increase developer velocity by 25% (measured by story points per sprint)',
];
